<template>
    <FormulateForm
        v-model="model"
        :schema="schemaComputed"
        :ref="name"
        :name="name"
        :class="className"
        @submit="onSubmit"
    >
        <slot></slot>
    </FormulateForm>
</template>

<script>
import FormFieldProcessMixin from "@/components/forms/inputs/mixins/FormFieldProcessMixin";
import { cloneDeep } from "lodash";

export default {
    mixins: [FormFieldProcessMixin],
    props: {
        schema: {
            type: Array,
            required: true,
        },
        mode: {
            type: String,
            default: "edit",
        },
        layout: {
            type: String,
            default: "stacked",
        },
        fieldLayout: {
            type: String,
            default: "stacked",
        },
    },
    data() {
        return {
            model: {},
            name: null,
            onChangeTimeout: null,
        };
    },
    watch: {
        model: {
            handler() {
                this.onChange();
            },
            deep: true,
        },
        mode() {
            if (this.mode === 'edit') {
                this.$nextTick(() => this.autofocus())
            }
        },
    },
    computed: {
        schemaComputed() {
            return cloneDeep(this.schema)
                .map((field) => this.processField(field))
                .filter((field) => !!field);
        },
        metaComputed() {
            return Object.assign(
                {},
                this.$tenantInfo,
                this.$currentUserInfo,
                cloneDeep(this.meta)
            );
        },
        form() {
            return this.$refs[this.name];
        },
        context() {
            return this.form?.formContext;
        },
        isValid() {
            return this.context?.isValid;
        },
        className() {
            return `supplios-form supplios-form-${this.mode} supplios-form-${this.layout}`;
        },
    },
    methods: {
        setName() {
            const randomString = Math.random()
                .toString(36)
                .replace(/[^a-z0-9]+/g, "");

            this.name = `supplios_form_${randomString}`;
        },
        setValues(values) {
            this.$formulate.setValues(this.name, values);
        },
        reset(values) {
            this.$formulate.reset(this.name, values);
        },
        resetValidation() {
            this.$formulate.resetValidation(this.name);
        },
        checkIsValid() {
            return this.isValid;
        },
        onError(error) {
            this.$formulate.handle(error, this.name);
        },
        submit() {
            this.$formulate.submit(this.name);
        },
        onChange() {
            if (this.onChangeTimeout) clearTimeout(this.onChangeTimeout);

            const payload = {
                values: this.model,
                isValid: this.context?.isValid,
            };

            this.onChangeTimeout = setTimeout(
                () => this.$emit("change", payload),
                300
            );
        },
        onSubmit(values) {
            // handle inconsistent submit data for file input
            // see https://github.com/wearebraid/vue-formulate/issues/462
            Object.keys(values).forEach((key) => {
                if (
                    values[key] instanceof Object &&
                    Array.isArray(values[key].results)
                ) {
                    values[key] = values[key].results;
                }
            });

            const listener = this.$listeners.submit(values);
            return (
                listener instanceof Promise ? listener : Promise.resolve()
            ).then(() => values);
        },
        autofocus() {
            this.form.$el.querySelector("input[autofocus]")?.focus();
        },
    },
    mounted() {
        this.autofocus();
    },
    created() {
        this.setName();
    },
};
</script>

<style lang="sass">
.supplios-form
    display: flex
    flex-direction: column

    .formulate-input
        &[data-type="hidden"]
            display: none

    &.supplios-form-inline
        flex-direction: row
        .formulate-input-group,
        .formulate-input-group-repeatable
            display: flex
            flex-direction: row
        .supplios-form-field
            width: 100%
            min-width: 10rem
            margin-right: 1rem
            &:last-child
                margin-right: 0

    [disabled],
    .p-disabled,
    [disabled] .formulate-input-element-decorator
        opacity: 1 !important
        background-color: #f4f5f7 !important
        border-color: transparent !important
        .p-dropdown-trigger,
        .p-multiselect-trigger,
        .p-dropdown-clear-icon
            display: none !important

    .supplios-form-field
        &:last-child
            margin-bottom: 0
        .p-component
            display: block !important
        .p-button-icon-only .p-button-label
            display: none
        &[data-classification='select']
            .p-dropdown,
            .p-multiselect,
            .p-autocomplete
                display: inline-flex !important
                width: 100% !important
            .p-autocomplete-multiple-container,
            .p-autocomplete-input
                width: 100% !important
        &[data-classification='checkbox'],
        &[data-classification='radio']
            .p-component
                display: inline-block !important

    .supplios-form-field-datetime-help
        margin-bottom: 0 !important

    .supplios-form-field-inline:not([data-classification="box"]):not([data-classification="group"])
        > .formulate-input-wrapper
            display: flex
            align-items: center
            width: 100%
            label
                width: 30%
                margin-right: 5%
            .formulate-input-element
                width: 100%
            & + .formulate-input-help,
            & + .formulate-input-errors
                margin-left: 27%

    .p-component
        font-size: inherit !important
</style>